import { useQuery } from '@tanstack/react-query';
import { useTranslation } from '@govard/i18n-core/react';
import { getEnumLabel } from '../../../shared/format/enum-labels.js';
import { useCurrentScope } from '../../../shared/scope/useCurrentScope.js';
import { useEntityActivities } from '../../activity/hooks/useEntityActivities.js';
import { describeActivity } from '../../activity/lib/describeActivity.js';
import { useMembers } from '../../tasks/hooks/useTaskLookups.js';
import {
  getDealStageHistoryQueryKey,
  listDealStageHistory,
  type DealActivityItem,
} from '../api/deals.client.js';

export interface DealHistoryItem {
  id: string;
  kind: string;
  text: string;
  date: string;
  actor: string;
}

export function useDealHistory(dealId: string, fallback: DealActivityItem[] = []) {
  const { t } = useTranslation();
  const scope = useCurrentScope();
  const activities = useEntityActivities('deal', dealId);
  const stages = useQuery({
    queryKey: getDealStageHistoryQueryKey(scope?.workspaceId ?? 'anonymous', dealId),
    queryFn: () => listDealStageHistory(dealId),
    enabled: scope !== null,
    retry: false,
  });
  const members = useMembers();
  const actor = (id: string): string => {
    const member = members.data?.find((item) => item.userId === id || item.id === id);
    return member?.name || member?.email || '';
  };
  const source = activities.data ?? fallback;
  const items: DealHistoryItem[] = source
    .filter((item) => item.kind !== 'deal_stage_changed')
    .map((item) => ({
      id: item.id,
      kind: item.kind,
      text: describeActivity(item),
      date: item.occurredAt ?? item.createdAt,
      actor: actor(item.actor),
    }));
  if (stages.data) {
    items.push(
      ...stages.data.map((item) => ({
        id: `stage:${item.id}`,
        kind: 'deal_stage_changed',
        text: `${getEnumLabel('dealStage', item.fromStage, t)} → ${getEnumLabel('dealStage', item.toStage, t)}${item.reason ? ` · ${item.reason}` : ''}`,
        date: item.createdAt,
        actor: actor(item.changedBy),
      })),
    );
  } else {
    items.push(
      ...source
        .filter((item) => item.kind === 'deal_stage_changed')
        .map((item) => ({
          id: item.id,
          kind: item.kind,
          text:
            item.payload && 'fromStage' in item.payload && 'toStage' in item.payload
              ? `${getEnumLabel('dealStage', String(item.payload.fromStage), t)} → ${getEnumLabel('dealStage', String(item.payload.toStage), t)}`
              : describeActivity(item),
          date: item.occurredAt ?? item.createdAt,
          actor: actor(item.actor),
        })),
    );
  }
  return {
    items: items.sort((a, b) => Date.parse(b.date) - Date.parse(a.date)),
    stages: stages.data ?? [],
    isLoading: activities.isLoading || stages.isLoading,
    error: activities.error ?? stages.error,
    refetch: () => {
      void activities.refetch();
      void stages.refetch();
    },
  };
}
